import type { ReactNode } from "react";

import {
  SITE_URL,
  baseCrumbs,
  orgNode,
  faqJsonld,
  breadcrumbJsonld,
  webPageNode,
  type LearnFAQ,
  type LearnPageData,
} from "./learnShared";

const P = (s: ReactNode): ReactNode => <p>{s}</p>;

const faqs: LearnFAQ[] = [
  {
    question: "Can SAINT® TMS be done as an outpatient?",
    answer:
      "Yes. SAINT® is delivered in an outpatient clinic while patients are awake. No anesthesia or sedation is used, and there is no overnight hospital stay. Neuro Wellness TMS Centers of America provides SAINT® in a private outpatient setting in Coral Springs, Florida, with dedicated treatment suites.",
  },
  {
    question: "Do I need to be hospitalized for SAINT®?",
    answer:
      "No. SAINT® does not require hospitalization. Patients sit in a treatment chair while a coil delivers magnetic pulses to the scalp, and they can return to normal activities after each session. There is no operating room, no recovery period requiring observation, and no overnight stay.",
  },
  {
    question: "Can I drive myself to and from SAINT® treatment?",
    answer:
      "Yes. Because SAINT® is non-invasive and does not involve anesthesia or sedation, patients can generally drive themselves to and from the clinic each day. If you experience a side effect that affects your ability to drive, such as a significant headache, tell the treatment team.",
  },
  {
    question:
      "What is the difference between outpatient SAINT® and hospital-based treatments?",
    answer:
      "SAINT® is delivered in an outpatient clinic, not a hospital. This is different from hospital-based brain stimulation treatments like electroconvulsive therapy (ECT), which requires anesthesia and a recovery period. SAINT® patients remain awake throughout and do not need an escort home after treatment.",
  },
  {
    question: "What do patients do between SAINT® sessions?",
    answer:
      "Each SAINT® day includes ten sessions of about ten minutes each, separated by roughly 50-minute breaks. Most patients read, work on a laptop, rest, or socialize during the breaks. Neuro Wellness provides private treatment suites where patients can relax comfortably between sessions.",
  },
];

export const pageOutpatientSaint: LearnPageData = {
  slug: "outpatient-saint-tms",
  title: "Outpatient SAINT® TMS | Neuro Wellness",
  description:
    "SAINT® TMS is delivered in a private outpatient setting — no hospitalization, no anesthesia, no overnight stay. Learn what outpatient SAINT® treatment is like at Neuro Wellness in Coral Springs, FL.",
  canonical: "/learn/outpatient-saint-tms",
  h1: "Can SAINT® TMS Be Done as an Outpatient?",
  breadcrumbLabel: "Outpatient SAINT® TMS",
  crumbs: baseCrumbs("Outpatient SAINT® TMS"),
  shortAnswer:
    "Yes. SAINT® (Stanford Accelerated Intelligent Neuromodulation Therapy) is delivered in an outpatient clinic while patients are awake. No anesthesia or sedation is used, and there is no overnight hospital stay. Neuro Wellness TMS Centers of America provides SAINT® in a private outpatient setting in Coral Springs, Florida, with dedicated treatment suites where patients can relax between sessions. This is different from hospital-based brain stimulation treatments like electroconvulsive therapy (ECT), which requires anesthesia and a recovery period.",
  keyTakeaways: [
    "SAINT® is an outpatient treatment — no hospitalization or overnight stay required.",
    "No anesthesia or sedation is used; patients remain awake throughout.",
    "Patients can generally drive themselves to and from the clinic each day.",
    "Each treatment day includes ten short sessions with 50-minute breaks between them.",
    "Neuro Wellness provides SAINT® in a private outpatient environment with dedicated suites.",
  ],

  sections: [
    {
      heading: "What Outpatient SAINT® Treatment Means",
      body: (
        <>
          {P(
            "SAINT® is delivered in an outpatient clinic, not a hospital. Patients sit in a treatment chair while an electromagnetic coil placed on the scalp delivers magnetic pulses to stimulate a specific brain region. The patient is awake and alert throughout, and can return to normal activities after each session.",
          )}
          {P(
            "There is no operating room, no anesthesia, no sedation, and no recovery period requiring medical observation. This makes SAINT® fundamentally different from hospital-based treatments like electroconvulsive therapy (ECT), which requires general anesthesia and a supervised recovery period.",
          )}
        </>
      ),
    },
    {
      heading: "The Outpatient Treatment Day",
      body: (
        <>
          {P(
            "Each SAINT® treatment day includes ten sessions of about ten minutes each, separated by roughly 50-minute breaks. The breaks are a deliberate part of the protocol — they allow the brain to respond to the stimulation pattern. Over five consecutive days, this totals 50 sessions.",
          )}
          {P(
            "During the breaks, most patients read, work on a laptop, rest, or socialize. Because the sessions are short and the breaks are built in, patients spend most of each treatment day at the clinic but are not in active treatment for the entire time.",
          )}
          {P(
            <>
              See our detailed guide:{" "}
              <a
                href="/learn/what-to-expect-during-saint-tms"
                className="font-medium text-accent underline underline-offset-2 hover:opacity-80"
              >
                What Happens During the 5 Days of SAINT®
              </a>
              .
            </>,
          )}
        </>
      ),
    },
    {
      heading: "Private Outpatient Environment at Neuro Wellness",
      body: (
        <>
          {P(
            "Neuro Wellness TMS Centers of America provides SAINT® in a private outpatient setting designed for patient comfort. The clinic offers dedicated treatment suites where patients can relax between sessions, rather than sitting in a shared waiting room.",
          )}
          {P(
            "This private environment is part of what makes the five-day accelerated schedule manageable. Patients have a comfortable space to work, rest, or spend time with a companion during the 50-minute breaks between sessions.",
          )}
        </>
      ),
    },
    {
      heading: "Driving and Transportation",
      body: (
        <>
          {P(
            "Because SAINT® does not involve anesthesia or sedation and does not impair alertness, patients can generally drive themselves to and from the clinic each day. This is one of the main practical differences between SAINT® and ECT, which requires an escort home after each treatment.",
          )}
          {P(
            "If you experience a side effect that affects your ability to drive, such as a significant headache or dizziness, tell the treatment team. A companion is optional rather than required.",
          )}
        </>
      ),
    },
    {
      heading: "Outpatient Versus Hospital-Based Treatment",
      body: (
        <>
          {P(
            "SAINT® is an outpatient treatment. ECT, by contrast, is a hospital-based procedure that requires general anesthesia, induces a controlled seizure, and involves a supervised recovery period. Both are legitimate treatments for severe depression, but they differ significantly in setting, invasiveness, and recovery.",
          )}
          {P(
            "SAINT® is non-invasive, does not require anesthesia, and does not involve a seizure. Patients remain awake, can drive themselves home, and return to normal activities after each session. This is why SAINT® can be delivered in an outpatient clinic rather than a hospital.",
          )}
        </>
      ),
    },
    {
      heading: "Neuro Wellness TMS Centers of America",
      body: (
        <>
          {P(
            "Neuro Wellness TMS Centers of America is an outpatient mental health treatment center at 9734 W Sample Rd, 2nd Floor, Suite B, Coral Springs, FL 33065. The clinic offers SAINT® on the Magnus Neuromodulation System, along with traditional TMS, SPRAVATO® (esketamine), Prism for PTSD, Proliv Rx, IV ketamine, psychotherapy, and medication management.",
          )}
          {P(
            "To learn more or schedule a consultation, call 954-827-9793 or request a consultation online.",
          )}
        </>
      ),
    },
  ],

  faqs,
  sources: [
    {
      citation:
        "Cole EJ, et al. Stanford Neuromodulation Therapy (SNT): a double-blind randomized controlled trial. Am J Psychiatry. 2022;179(2):132-141.",
      url: "https://doi.org/10.1176/appi.ajp.2021.20101429",
    },
    {
      citation:
        "U.S. Food and Drug Administration. 510(k) Premarket Notification — Magnus Neuromodulation System with SAINT Technology (K220177, September 2022).",
      url: "https://www.accessdata.fda.gov/scripts/cdrh/cfdocs/cfpmn/pmn.cfm",
    },
  ],

  reviewBlock: {
    writtenBy: "Neuro Wellness TMS Centers of America",
    reviewedBy: "Daniel Pietras, MD",
    published: "November 3, 2026",
    lastReviewed: "November 3, 2026",
    lastUpdated: "November 3, 2026",
  },
  schemaJson: {
    "@context": "https://schema.org",
    "@graph": [
      webPageNode({
        id: `${SITE_URL}/learn/outpatient-saint-tms#webpage`,
        name: "Can SAINT® TMS Be Done as an Outpatient?",
        url: `${SITE_URL}/learn/outpatient-saint-tms`,
        description:
          "SAINT® TMS is delivered in a private outpatient setting — no hospitalization, no anesthesia, no overnight stay. Learn what outpatient SAINT® treatment is like.",
        about: [
          { "@id": `${SITE_URL}/learn/outpatient-saint-tms#therapy` },
          { "@id": `${SITE_URL}/#condition-mdd` },
        ],
      }),
      orgNode,
      {
        "@type": "MedicalTherapy",
        "@id": `${SITE_URL}/learn/outpatient-saint-tms#therapy`,
        name: "Stanford Accelerated Intelligent Neuromodulation Therapy (SAINT®)",
        alternateName: ["SAINT TMS", "Stanford Neuromodulation Therapy (SNT)"],
        description:
          "Accelerated, fMRI-guided intermittent theta burst stimulation delivered on the Magnus Neuromodulation System with SAINT Technology (FDA 510(k) K220177): 10 sessions per day for 5 consecutive days.",
        relevantSpecialty: "Psychiatric",
        procedureType: "Noninvasive",
      },
      {
        "@type": "MedicalCondition",
        "@id": `${SITE_URL}/#condition-mdd`,
        name: "Major depressive disorder",
        code: { "@type": "MedicalCode", code: "D003865", codingSystem: "MeSH" },
      },
      faqJsonld(faqs, `${SITE_URL}/learn/outpatient-saint-tms#faq`),
      breadcrumbJsonld(baseCrumbs("Outpatient SAINT® TMS")),
    ],
  },
  relatedLinks: [
    {
      title: "SAINT® Accelerated TMS",
      href: "/saint",
      description: "The 5-day, fMRI-guided protocol at our clinic",
    },
    {
      title: "What to Expect During SAINT®",
      href: "/learn/what-to-expect-during-saint-tms",
      description: "A day-by-day patient walkthrough",
    },
    {
      title: "Find a Verified SAINT® Provider",
      href: "/find-a-verified-saint-provider",
      description: "Verification steps and what to ask",
    },
    {
      title: "SAINT® TMS in Florida",
      href: "/saint-tms-florida",
      description: "SAINT® access for Florida patients",
    },
    {
      title: "Schedule a Consultation",
      href: "/am-i-a-candidate",
      description: "Request a free consultation with our team",
    },
  ],
};
